import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { DownloadModel } from 'src/app/models/download.model';
import { FileUploadStatus, UploadModel } from 'src/app/models/upload.model';
import { TrimStringPipe } from 'src/app/pipes/trim-string.pipe';
import { CommonService } from 'src/app/services/common.service';
import { DownloadService } from 'src/app/services/download.service';
import { UploadService } from 'src/app/services/upload.service';

export enum FileUploadState {
  UPLOADED = 'uploaded',
  PENDING = 'pending',
  FAILED = 'failed',
  COMPLETED = 'completed',
  UPLODAING = 'uploading',
}

export enum FileDownloadState {
  DOWNLOADED = 'downloaded',
  PENDING = 'pending',
  FAILED = 'failed',
}

export interface FileResponse {
  documentName: string;
  icon: string | null;
  percentage: number;
  state: FileUploadState;
}

/**
 * TbtkEmptyFileModule is the content edited to upload files or folders on the blank page.
 *
 * Just import it to your project of `@NgModule` import section.
 *
 * <sub>app.module.ts</sub>
 *
 * ```typescript
 * @NgModule({
 *   imports: [TbtkEmptyFileModule],
 * })
 * export class AppModule {}
 * ```
 */

@Component({
  selector: 'tbtk-file-upload-animation',
  templateUrl: './file-upload-animation.component.html',
  styleUrls: ['./file-upload-animation.component.scss'],
  standalone: true,
  imports: [CommonModule, TranslateModule, TranslateModule],
  providers: [TrimStringPipe],
})
export class FileUploadAnimationComponent {
  @Input() uploadItems!: UploadModel[];
  @Input() downloadItems!: DownloadModel[];
  @Output() closeFileUpload: EventEmitter<void> = new EventEmitter<void>();
  public fileUploadState = FileUploadState;
  public fileDownloadState = FileDownloadState;
  public loading = false;
  public success = false; //bu durumlar final resultlar için //json içinde de ayrı kontrolleri var burda ayar yapmak gerekiyor
  public remainingTime = 0;
  public multiFile = false;
  public collapse = true;
  public uploadStatus: FileUploadStatus = {} as FileUploadStatus;

  constructor(public downloadService: DownloadService, public uploadService: UploadService, public trimStringPipe: TrimStringPipe, public commonService: CommonService) {
    this.uploadService.uploadItems$.subscribe((items) => {
      if (items.every((item) => item.status === 'uploaded')) {
        commonService.rebindListener.next();
      }
    });
  }

  cancelAllUploadsAndDownloads(): void {
    this.uploadService.cancelUploads();
    this.downloadService.cancelDownloads();
  }

  retryAllUploadsAndDownloads(): void {
    this.uploadService.retryUploads();
    this.downloadService.retryAllDownloads();
  }

  getProgress(data: UploadModel): number {
    if (data.data.length === 0) {
      return 0;
    }
    const sum = data.data.reduce((acc, obj) => acc + obj.progress, 0);

    return sum / data.data.length;
  }

  getProgressOfDownload(data: DownloadModel): number {
    return data.zippedObject ? this.getProgressOfDownloadObjects(data) : this.getProgressOfDownloadChunks(data);
  }

  getProgressOfDownloadChunks(data: DownloadModel): number {
    if (data.data.length === 0) {
      return 0;
    }
    const sum = data.data.reduce((acc, obj) => acc + obj.progress, 0);

    return sum > 0 ? (sum / data.totalSize) * 100 : 0;
  }

  getProgressOfDownloadObjects(data: DownloadModel): number {
    return (data.progress / data.totalSize) * 100;
  }

  uploadStatusMessage(): string {
    const uploaded = this.uploadItems.filter((item) => item.status === 'uploaded');
    const pending = this.uploadItems.filter((item) => item.status === 'pending');
    const failed = this.uploadItems.filter((item) => item.status === 'failed');
    const downloaded = this.downloadItems.filter((item) => item.status === 'downloaded');
    const pendingDownload = this.downloadItems.filter((item) => item.status === 'pending');
    const failedDownload = this.downloadItems.filter((item) => item.status === 'failed');
    const totalObjects = this.uploadItems.length + this.downloadItems.length;
    if (this.uploadItems.length > 0 && failed.length + uploaded.length === totalObjects) {
      if (failed.length > 1 && failed.length === totalObjects) {
        this.uploadStatus.message = this.commonService.getTranslatedMessage('FILES_CANNOT_BE_UPLOADED');
        this.uploadStatus.status = 'error';
      } else if (failed.length > 1 && failed.length !== totalObjects) {
        this.uploadStatus.message = this.commonService.getTranslatedMessage('SOME_FILES_CANNOT_BE_UPLOADED');
        this.uploadStatus.status = 'error';
      } else {
        this.uploadStatus.message = this.commonService.getTranslatedMessage('FILE_UPLOAD_FAILED');
        this.uploadStatus.status = 'error';
      }
      if (uploaded.length > 1 && uploaded.length === totalObjects) {
        this.uploadStatus.message = uploaded.length + ' ' + this.commonService.getTranslatedMessage('FILE_UPLOADED_SUCCESSFULLY');
        this.uploadStatus.status = 'success';
      } else if (uploaded.length === 1 && uploaded.length === totalObjects) {
        this.uploadStatus.message = this.commonService.getTranslatedMessage('FILE_UPLOADED_SUCCESSFULLY');
        this.uploadStatus.status = 'success';
      }
    } else if (this.downloadItems.length > 0 && failedDownload.length + downloaded.length === totalObjects) {
      if (failed.length > 0 && this.downloadItems.length) {
        this.uploadStatus.message = this.commonService.getTranslatedMessage('FILE_DOWNLOAD_FAILED');
        this.uploadStatus.status = 'error';
      }
      if (downloaded.length > 0 && downloaded.length === totalObjects) {
        this.uploadStatus.message = this.commonService.getTranslatedMessage('FILE_DOWNLOADED_SUCCESSFULLY');
        this.uploadStatus.status = 'success';
      }
    } else {
      this.uploadStatus = {} as FileUploadStatus;
    }
    if (this.downloadItems.length > 0 && this.uploadItems.length > 0) {
      return this.commonService.getTranslatedMessage('FILE_TRANSFER');
    } else if (this.downloadItems.length > 0 && this.uploadItems.length === 0) {
      if (pendingDownload.length > 0) {
        return pendingDownload.length + ' ' + this.commonService.getTranslatedMessage('FILE_DOWNLOADING');
      }
      if (pendingDownload.length < 0 && failedDownload.length > 0) {
        return failedDownload.length + ' ' + this.commonService.getTranslatedMessage('FILE_COULD_NOT_BE_DOWNLOADED');
      }
      if (pendingDownload.length < 0 && downloaded.length > 0) {
        return downloaded.length + ' ' + this.commonService.getTranslatedMessage('FILE_DOWNLOADED');
      } else {
        return this.commonService.getTranslatedMessage('FILE_TRANSFER');
      }
    } else if (this.downloadItems.length === 0 && this.uploadItems.length > 0) {
      if (pending.length > 0 || failed.length > 0 || uploaded.length > 0) {
        if (pending.length > 0) {
          return pending.length + ' ' + this.commonService.getTranslatedMessage('FILE_UPLOADING');
        }
        if (pending.length < 0 && failed.length > 0) {
          return failed.length + ' ' + this.commonService.getTranslatedMessage('FILE_COULD_NOT_BE_UPLOADED');
        }
        if (pending.length < 0 && uploaded.length > 0) {
          return uploaded.length + ' ' + this.commonService.getTranslatedMessage('FILE_UPLOADED');
        } else {
          return this.commonService.getTranslatedMessage('FILE_TRANSFER');
        }
      } else {
        return this.commonService.getTranslatedMessage('FILE_TRANSFER');
      }
    } else {
      return this.commonService.getTranslatedMessage('FILE_TRANSFER');
    }
  }

  calculateRemainingTime(): void {
    let remainingTime = 0;
    const promise = new Promise((resolve, reject) => {
      this.uploadItems.forEach((item) => {
        item.data.forEach((data) => {
          remainingTime += data.remainingTime;
        });
      });
      resolve(remainingTime);
    });
    promise.then((remainingTime) => {});
  }

  close(): void {
    this.closeFileUpload.emit();
    this.cancelAllUploadsAndDownloads();
  }
}
